import { useEffect, useRef, useCallback, useState } from "react";

// YouTube IFrame API type definitions
interface YTPlayer {
  playVideo: () => void;
  pauseVideo: () => void;
  stopVideo: () => void;
  getPlayerState: () => number;
  setVolume: (volume: number) => void;
  getVolume: () => number;
  mute: () => void;
  unMute: () => void;
  isMuted: () => boolean;
  destroy: () => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  setPlaybackRate: (rate: number) => void;
  getPlaybackRate: () => number;
  setPlaybackQuality: (quality: string) => void;
  getPlaybackQuality: () => string;
  getAvailableQualityLevels: () => string[];
  getIframe: () => HTMLIFrameElement;
  requestFullscreen?: () => void;
  exitFullscreen?: () => void;
}

interface YTPlayerOptions {
  videoId: string;
  playerVars?: {
    autoplay?: number;
    rel?: number;
    modestbranding?: number;
    enablejsapi?: number;
    origin?: string;
    iv_load_policy?: number;
  };
  events?: {
    onReady?: () => void;
    onStateChange?: (event: { data: number }) => void;
  };
}

interface YTNamespace {
  Player: new (element: HTMLElement, options: YTPlayerOptions) => YTPlayer;
  PlayerState: {
    UNSTARTED: number;
    ENDED: number;
    PLAYING: number;
    PAUSED: number;
    BUFFERING: number;
    CUED: number;
  };
}

declare global {
  interface Window {
    YT: YTNamespace;
    onYouTubeIframeAPIReady: () => void;
  }
}

interface UseYouTubePlayerOptions {
  videoId: string | null;
  autoPlay?: boolean;
  onReady?: () => void;
  onStateChange?: (isPlaying: boolean) => void;
  onEnd?: () => void;
}

export const useYouTubePlayer = ({
  videoId,
  autoPlay = false,
  onReady,
  onStateChange,
  onEnd,
}: UseYouTubePlayerOptions) => {
  const playerRef = useRef<YTPlayer | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isApiReady, setIsApiReady] = useState(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false);

  // Use refs to store callbacks to avoid recreating player on callback changes
  const onReadyRef = useRef(onReady);
  const onStateChangeRef = useRef(onStateChange);
  const onEndRef = useRef(onEnd);

  // Keep refs updated
  useEffect(() => {
    onReadyRef.current = onReady;
    onStateChangeRef.current = onStateChange;
    onEndRef.current = onEnd;
  }, [onReady, onStateChange, onEnd]);

  // Load YouTube IFrame API
  useEffect(() => {
    if (window.YT && window.YT.Player) {
      setIsApiReady(true);
      return;
    }

    const existingScript = document.querySelector(
      'script[src="https://www.youtube.com/iframe_api"]'
    );

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      document.body.appendChild(script);
    }

    window.onYouTubeIframeAPIReady = () => {
      setIsApiReady(true);
    };
  }, []);

  // Initialize or update player - only depends on videoId and isApiReady
  useEffect(() => {
    if (!isApiReady || !containerRef.current || !videoId) {
      return;
    }

    // Destroy existing player if video changes
    if (playerRef.current) {
      playerRef.current.destroy();
      playerRef.current = null;
      setIsPlayerReady(false);
    }

    const initializePlayer = () => {
      // Get container dimensions for responsive sizing
      const containerRect = containerRef.current!.getBoundingClientRect();
      const width = Math.max(containerRect.width || 640, 320);
      const height = Math.max(containerRect.height || 360, 180);

      playerRef.current = new window.YT.Player(containerRef.current!, {
        width: width.toString(),
        height: height.toString(),
        videoId,
        playerVars: {
          autoplay: autoPlay ? 1 : 0,
          rel: 0,
          modestbranding: 1,
          enablejsapi: 1,
          origin: window.location.origin,
          iv_load_policy: 3, // Disable annotations and preview thumbnails
        },
        events: {
          onReady: () => {
            setIsPlayerReady(true);
            onReadyRef.current?.();
          },
          onStateChange: (event: { data: number }) => {
            if (event.data === window.YT.PlayerState.PLAYING) {
              onStateChangeRef.current?.(true);
            } else if (
              event.data === window.YT.PlayerState.PAUSED ||
              event.data === window.YT.PlayerState.ENDED
            ) {
              onStateChangeRef.current?.(false);
            }
            if (event.data === window.YT.PlayerState.ENDED) {
              onEndRef.current?.();
            }
          },
        },
      });
    };

    initializePlayer();

    // Handle resize to keep player dimensions in sync
    const handleResize = () => {
      if (playerRef.current && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const width = Math.max(containerRect.width || 640, 320);
        const height = Math.max(containerRect.height || 360, 180);

        // Resize the iframe directly by setting its style
        const iframe = playerRef.current.getIframe();
        if (iframe) {
          iframe.style.width = `${width}px`;
          iframe.style.height = `${height}px`;
        }
      }
    };

    // Debounced resize handler
    let resizeTimeout: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 250);
    };

    window.addEventListener('resize', debouncedResize);

    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(resizeTimeout);
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
        setIsPlayerReady(false);
      }
    };
  }, [isApiReady, videoId]);

  const play = useCallback(() => {
    if (isPlayerReady && playerRef.current) {
      playerRef.current.playVideo();
    }
  }, [isPlayerReady]);

  const pause = useCallback(() => {
    if (isPlayerReady && playerRef.current) {
      playerRef.current.pauseVideo();
    }
  }, [isPlayerReady]);

  const togglePlayPause = useCallback(() => {
    const player = playerRef.current;
    if (!player || !isPlayerReady || typeof player.getPlayerState !== 'function') return;

    try {
      const state = player.getPlayerState();
      if (state === window.YT.PlayerState.PLAYING) {
        player.pauseVideo();
      } else {
        player.playVideo();
      }
    } catch (e) {
      // Player may not be ready
    }
  }, [isPlayerReady]);

  const setVolume = useCallback((volume: number) => {
    if (isPlayerReady && playerRef.current && typeof playerRef.current.setVolume === 'function') {
      playerRef.current.setVolume(volume);
    }
  }, [isPlayerReady]);

  const mute = useCallback(() => {
    if (isPlayerReady && playerRef.current && typeof playerRef.current.mute === 'function') {
      playerRef.current.mute();
    }
  }, [isPlayerReady]);

  const unmute = useCallback(() => {
    if (isPlayerReady && playerRef.current && typeof playerRef.current.unMute === 'function') {
      playerRef.current.unMute();
    }
  }, [isPlayerReady]);

  const getIsMuted = useCallback(() => {
    if (isPlayerReady && playerRef.current && typeof playerRef.current.isMuted === 'function') {
      return playerRef.current.isMuted();
    }
    return false;
  }, [isPlayerReady]);

  const setMuted = useCallback((isMuted: boolean) => {
    if (!isPlayerReady || !playerRef.current) return;
    if (isMuted && typeof playerRef.current.mute === 'function') {
      playerRef.current.mute();
    } else if (typeof playerRef.current.unMute === 'function') {
      playerRef.current.unMute();
    }
  }, [isPlayerReady]);

  const getCurrentTime = useCallback(() => {
    if (isPlayerReady && playerRef.current && typeof playerRef.current.getCurrentTime === 'function') {
      return playerRef.current.getCurrentTime();
    }
    return 0;
  }, [isPlayerReady]);

  const getDuration = useCallback(() => {
    if (isPlayerReady && playerRef.current && typeof playerRef.current.getDuration === 'function') {
      return playerRef.current.getDuration();
    }
    return 0;
  }, [isPlayerReady]);

  const seekTo = useCallback((seconds: number) => {
    if (isPlayerReady && playerRef.current && typeof playerRef.current.seekTo === 'function') {
      playerRef.current.seekTo(seconds, true);
    }
  }, [isPlayerReady]);

  const setPlaybackRate = useCallback((rate: number) => {
    if (isPlayerReady && playerRef.current && typeof playerRef.current.setPlaybackRate === 'function') {
      playerRef.current.setPlaybackRate(rate);
    }
  }, [isPlayerReady]);

  const getPlaybackRate = useCallback(() => {
    if (isPlayerReady && playerRef.current && typeof playerRef.current.getPlaybackRate === 'function') {
      return playerRef.current.getPlaybackRate();
    }
    return 1;
  }, [isPlayerReady]);

  const setPlaybackQuality = useCallback((quality: string) => {
    if (isPlayerReady && playerRef.current && typeof playerRef.current.setPlaybackQuality === 'function') {
      playerRef.current.setPlaybackQuality(quality);
    }
  }, [isPlayerReady]);

  const getPlaybackQuality = useCallback(() => {
    if (isPlayerReady && playerRef.current && typeof playerRef.current.getPlaybackQuality === 'function') {
      return playerRef.current.getPlaybackQuality();
    }
    return 'auto';
  }, [isPlayerReady]);

  const getAvailableQualityLevels = useCallback(() => {
    if (isPlayerReady && playerRef.current && typeof playerRef.current.getAvailableQualityLevels === 'function') {
      return playerRef.current.getAvailableQualityLevels();
    }
    return [];
  }, [isPlayerReady]);

  const togglePictureInPicture = useCallback(async () => {
    if (!isPlayerReady || !playerRef.current) return false;

    try {
      const iframe = playerRef.current.getIframe();
      if (!iframe) return false;

      // Check if PiP is supported
      if (!document.pictureInPictureEnabled) {
        return false;
      }

      // If already in PiP, exit
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        return false;
      }

      // For YouTube, we need to create a canvas-based approach or use the native controls
      // Since YouTube blocks direct PiP access, we'll show a toast with instructions
      return true;
    } catch (error) {
      console.error('PiP error:', error);
      return false;
    }
  }, [isPlayerReady]);

  const isPiPSupported = useCallback(() => {
    return document.pictureInPictureEnabled ?? false;
  }, []);

  return {
    containerRef,
    play,
    pause,
    togglePlayPause,
    setVolume,
    mute,
    unmute,
    setMuted,
    getIsMuted,
    getCurrentTime,
    getDuration,
    seekTo,
    setPlaybackRate,
    getPlaybackRate,
    setPlaybackQuality,
    getPlaybackQuality,
    getAvailableQualityLevels,
    togglePictureInPicture,
    isPiPSupported,
    isApiReady,
    isPlayerReady,
  };
};
